import React, { useState, useEffect } from "react";

// Matches the modal overlay background — used to paint the browser chrome
// (URL bar, toolbar) the same colour so no gap shows on mobile.
const OVERLAY_BG = "#040a14";

export default function TermsAcceptanceModal({ onAccept, onDecline, loading }) {
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    // Lock body scroll so the mobile URL bar doesn't shift the viewport while
    // the modal is open (which would leave an unblurred strip at top/bottom).
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Paint the browser chrome (theme-color) to match the overlay so the
    // area behind the URL bar / toolbar looks seamless.
    const metaTheme = document.querySelector('meta[name="theme-color"]');
    const prevColor = metaTheme?.getAttribute("content");
    if (metaTheme) metaTheme.setAttribute("content", OVERLAY_BG);

    return () => {
      document.body.style.overflow = prev;
      if (metaTheme && prevColor) metaTheme.setAttribute("content", prevColor);
    };
  }, []);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(4,10,20,0.85)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        padding: "16px",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "520px",
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: "16px",
          boxShadow: "0 24px 80px rgba(0,0,0,0.6)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "24px 28px 20px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <h2
            style={{
              fontSize: "17px",
              fontWeight: "700",
              color: "var(--text-heading)",
              margin: 0,
              lineHeight: "1.3",
            }}
          >
            Review & Accept Terms
          </h2>
          <p
            style={{
              fontSize: "13px",
              color: "var(--text-secondary)",
              margin: "4px 0 0",
            }}
          >
            Before using SaffronOS, please review and accept our policies.
          </p>
        </div>

        {/* Body */}
        <div style={{ padding: "24px 28px" }}>
          {/* Single combined checkbox */}
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
                cursor: "pointer",
                padding: "14px 16px",
                borderRadius: "9px",
                background: checked ? "rgba(6,182,212,0.05)" : "transparent",
                border: `1px solid ${checked ? "rgba(6,182,212,0.25)" : "var(--border)"}`,
                transition: "all 0.15s",
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                style={{
                  width: "17px",
                  height: "17px",
                  flexShrink: 0,
                  marginTop: "2px",
                  accentColor: "var(--teal-bright)",
                  cursor: "pointer",
                }}
              />
              <span
                style={{
                  fontSize: "13px",
                  color: "var(--text-secondary)",
                  lineHeight: "1.6",
                }}
              >
                I have read and agree to the{" "}
                <a
                  href="/saffronos-terms.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "var(--teal-bright)",
                    textDecoration: "none",
                    fontWeight: "600",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </a>
                {" "}and{" "}
                <a
                  href="/privacy-policy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "var(--teal-bright)",
                    textDecoration: "none",
                    fontWeight: "600",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </a>
              </span>
            </label>
          </div>

          {/* Buttons */}
          <div style={{ display: "flex", gap: "10px" }}>
            <button
              type="button"
              onClick={onDecline}
              disabled={loading}
              style={{
                flex: "0 0 auto",
                padding: "11px 18px",
                background: "transparent",
                border: "1px solid var(--border)",
                borderRadius: "9px",
                color: "var(--text-secondary)",
                fontSize: "13px",
                fontWeight: "500",
                cursor: loading ? "not-allowed" : "pointer",
                transition: "all 0.15s",
              }}
            >
              Sign out
            </button>
            <button
              type="button"
              onClick={onAccept}
              disabled={!checked || loading}
              style={{
                flex: 1,
                padding: "11px 20px",
                background: checked
                  ? "var(--gradient-btn)"
                  : "rgba(255,255,255,0.06)",
                border: `1px solid ${checked ? "transparent" : "var(--border)"}`,
                borderRadius: "9px",
                color: checked ? "white" : "var(--text-muted)",
                fontSize: "14px",
                fontWeight: "600",
                cursor: !checked || loading ? "not-allowed" : "pointer",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                letterSpacing: "0.01em",
              }}
            >
              {loading ? (
                <>
                  <span
                    className="spinner"
                    style={{ width: "14px", height: "14px", borderWidth: "2px" }}
                  />
                  Saving...
                </>
              ) : (
                "Accept & Continue →"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
